import React, { useState, useRef, useEffect } from "react";
import { usePassioApi } from "../hooks/usePassioApi";
import { IngredientExtract } from "../types";
import { useToolHistory, TOOL_HISTORY_KEYS } from "../hooks/useToolHistory";
import { ToolHistorySection } from "./ToolHistorySection";
import { useBackgroundTasks } from "../contexts/BackgroundTaskContext";
import { useToast } from "../contexts/ToastContext";
import { tokenUsageTracker } from "../services/tokenUsageTracker";

interface PredictIngredientsData {
    ingredients: IngredientExtract[];
    inputLength: number;
    message?: string;
}

export const PredictIngredients: React.FC = () => {
    const { client } = usePassioApi();
    const { addTask, updateTask } = useBackgroundTasks();
    const { showToast } = useToast();
    const [inputText, setInputText] = useState("");
    const [message, setMessage] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<IngredientExtract[] | null>(null);
    const [isInputCollapsed, setIsInputCollapsed] = useState(false);
    const resultsRef = useRef<HTMLDivElement>(null);

    const {
        history,
        addToHistory,
        clearHistory,
        loadFromHistory,
        selectedHistoryId
    } = useToolHistory<PredictIngredientsData>({
        storageKey: TOOL_HISTORY_KEYS.INGREDIENT_PREDICTOR,
        maxEntries: 10
    }, (data) => {
        setResults(data.ingredients);
        setIsInputCollapsed(true);
        setError(null);
    });

    const handleSubmit = async () => {
        if (!client || !inputText.trim()) return;

        setIsProcessing(true);
        setError(null);
        setResults(null);

        // Show start toast
        showToast({
            message: "Predicting next ingredients...",
            type: "info",
            duration: 3000,
        });

        // Add task to background tasks
        const taskId = addTask({
            type: "ingredient-prediction" as any,
            status: "running",
            message: "Predicting next ingredients for your recipe",
        });

        try {
            // Prepend message to the input text if message exists
            const content = message.trim() 
                ? `${message.trim()}\n\n${inputText.trim()}`
                : inputText.trim();

            // Make request to predict ingredients
            const response = await client.post<IngredientExtract[]>(
                "/v2/products/sdk/tools/predictIngredients",
                { 
                    content: content
                },
                undefined,
                { ttl: 0 } // No caching for predictions
            );

            const resultsArray = Array.isArray(response) ? response : [];
            setResults(resultsArray);
            
            // Add to history
            if (resultsArray.length > 0) {
                addToHistory({
                    ingredients: resultsArray,
                    inputLength: inputText.length,
                    message: message.trim() || undefined
                });
                setIsInputCollapsed(true);
            }

            // Get token usage data
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "completed",
                completedAt: new Date(),
                tokenUsage: usageData?.requestUsage,
            });

            // Show success toast
            showToast({
                message: `Predicted ${resultsArray.length} ingredients!`,
                type: "success",
                duration: 3000,
            });

            // Show token usage if available
            if (usageData && usageData.requestUsage > 0) {
                setTimeout(() => {
                    showToast({
                        message: `Used ${usageData.requestUsage.toLocaleString()} tokens`,
                        type: "info",
                        duration: 5000,
                    });
                }, 500);
            }
        } catch (err) {
            console.error("Error predicting ingredients:", err);
            const errorMessage = err instanceof Error ? err.message : "Failed to predict ingredients";
            setError(errorMessage);

            // Get token usage data for error tracking
            const usageData = tokenUsageTracker.getUsageData();

            // Update task status
            updateTask(taskId, {
                status: "failed",
                completedAt: new Date(),
                error: errorMessage,
                tokenUsage: usageData?.requestUsage,
            });

            // Show error toast
            showToast({
                message: errorMessage,
                type: "error",
                duration: 5000,
            });
        } finally {
            setIsProcessing(false);
        }
    };

    // Auto-scroll to results when they appear
    useEffect(() => {
        if (results && resultsRef.current) {
            setTimeout(() => {
                resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [results]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit();
        }
    };

    const handleReset = () => {
        setInputText("");
        setMessage("");
        setResults(null);
        setError(null);
        setIsInputCollapsed(false);
    };

    return (
        <div>
            {/* Collapsible Input Section */}
            <div style={{
                marginBottom: '24px',
                backgroundColor: isInputCollapsed ? '#f9fafb' : 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                padding: isInputCollapsed ? '16px' : '24px',
                transition: 'all 0.3s ease',
            }}>
                {isInputCollapsed ? (
                    // Collapsed view
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                        }}
                        onClick={() => setIsInputCollapsed(false)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '20px' }}>✍️</span>
                                <div>
                                    <div style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#111827',
                                    }}>
                                        Recipe Text ({inputText.length} characters)
                                    </div>
                                    {message && (
                                        <div style={{
                                            fontSize: '14px',
                                            color: '#6b7280',
                                            marginTop: '2px',
                                        }}>
                                            Context: {message}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleReset();
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fecaca';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fee2e2';
                                    }}
                                >
                                    New Prediction
                                </button>
                                <span style={{
                                    fontSize: '14px',
                                    color: '#6b7280',
                                }}>
                                    ▼
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    // Expanded view
                    <div>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <span>✍️</span>
                            Enter Recipe or Meal Description
                        </h3>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginBottom: '16px',
                            lineHeight: '1.5',
                        }}>
                            Type or paste a recipe, meal description, or list of ingredients. Our AI will predict and extract detailed ingredient information.
                        </p>
                        
                        <textarea
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Example: Grilled chicken breast with steamed broccoli and quinoa salad..."
                            style={{
                                width: '100%',
                                minHeight: '150px',
                                padding: '12px',
                                fontSize: '14px',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                lineHeight: '1.5',
                                transition: 'border-color 0.2s',
                            }}
                            onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#3b82f6';
                                e.currentTarget.style.outline = 'none';
                                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                            disabled={isProcessing}
                        />

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: '8px',
                        }}>
                            <span style={{
                                fontSize: '12px',
                                color: '#6b7280',
                            }}>
                                Tip: Press Ctrl+Enter to submit
                            </span>
                            <span style={{
                                fontSize: '12px',
                                color: '#6b7280',
                            }}>
                                {inputText.length} characters
                            </span>
                        </div>

                        {/* Optional Context Input */}
                        <div style={{ marginTop: '16px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px',
                            }}>
                                Additional Context (Optional)
                            </label>
                            <input
                                type="text"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="e.g., 'This serves 6 people' or 'Use metric measurements'"
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                    e.currentTarget.style.outline = 'none';
                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                disabled={isProcessing}
                            />
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={!inputText.trim() || isProcessing || !client}
                            style={{
                                marginTop: '16px',
                                width: '100%',
                                padding: '12px',
                                backgroundColor: !inputText.trim() || isProcessing ? '#e5e7eb' : '#3b82f6',
                                color: !inputText.trim() || isProcessing ? '#9ca3af' : 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: !inputText.trim() || isProcessing ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onMouseEnter={(e) => {
                                if (inputText.trim() && !isProcessing) {
                                    e.currentTarget.style.backgroundColor = '#2563eb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (inputText.trim() && !isProcessing) {
                                    e.currentTarget.style.backgroundColor = '#3b82f6';
                                }
                            }}
                        >
                            {isProcessing ? (
                                <>
                                    <span style={{ marginRight: '8px' }}>⏳</span>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <span style={{ marginRight: '8px' }}>🔮</span>
                                    Predict Ingredients
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* History Section */}
            <ToolHistorySection
                history={history}
                selectedHistoryId={selectedHistoryId}
                onLoadHistory={loadFromHistory}
                onClearHistory={clearHistory}
                formatDescription={(entry) => 
                    `${entry.data.ingredients.length} ingredients from ${entry.data.inputLength} chars`
                }
                title="Recent Predictions"
            />

            {/* Error Display */}
            {error && (
                <div style={{
                    padding: '16px',
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    border: '1px solid #fecaca',
                }}>
                    <span style={{ fontSize: '20px', marginTop: '-2px' }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>Error</div>
                        <div style={{ fontSize: '14px' }}>{error}</div>
                    </div>
                </div>
            )}

            {/* Results Display */}
            <div ref={resultsRef}>
                {results && results.length > 0 && (
                    <div style={{
                        padding: '24px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '12px',
                        border: '2px solid #10b981',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        animation: 'slideIn 0.3s ease-out',
                    }}>
                        <style>
                            {`
                                @keyframes slideIn {
                                    from {
                                        opacity: 0;
                                        transform: translateY(20px);
                                    }
                                    to {
                                        opacity: 1;
                                        transform: translateY(0);
                                    }
                                }
                            `}
                        </style>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#065f46',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}>
                            <span style={{ fontSize: '28px' }}>✅</span>
                            Successfully Predicted {results.length} Ingredient{results.length !== 1 ? 's' : ''}
                        </h3>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: '16px',
                        }}>
                            {results.map((ingredient, index) => (
                                <div
                                    key={index}
                                    style={{
                                        padding: '18px',
                                        backgroundColor: 'white',
                                        borderRadius: '8px',
                                        border: '1px solid #d1fae5',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                    }}
                                >
                                    <div style={{
                                        fontWeight: '600',
                                        color: '#111827',
                                        fontSize: '16px',
                                        marginBottom: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}>
                                        <span style={{ fontSize: '20px' }}>🥗</span>
                                        {ingredient.ingredientName}
                                    </div>
                                    
                                    {/* Meal and Component info */}
                                    {(ingredient.mealName || ingredient.componentName) && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#6b7280',
                                            marginBottom: '10px',
                                            display: 'flex',
                                            gap: '8px',
                                            flexWrap: 'wrap',
                                        }}>
                                            {ingredient.mealName && (
                                                <span style={{
                                                    backgroundColor: '#e0e7ff',
                                                    color: '#3730a3',
                                                    padding: '3px 10px',
                                                    borderRadius: '12px',
                                                    fontWeight: '500',
                                                }}>
                                                    🍽️ {ingredient.mealName}
                                                </span>
                                            )}
                                            {ingredient.componentName && (
                                                <span style={{
                                                    backgroundColor: '#fef3c7',
                                                    color: '#92400e',
                                                    padding: '3px 10px',
                                                    borderRadius: '12px',
                                                    fontWeight: '500',
                                                }}>
                                                    📦 {ingredient.componentName}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <div style={{
                                        fontSize: '14px',
                                        color: '#4b5563',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px',
                                        paddingTop: '8px',
                                        borderTop: '1px solid #f3f4f6',
                                    }}>
                                        {ingredient.portionQuantity > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ color: '#9ca3af', fontSize: '13px' }}>Portion:</span>
                                                <span style={{ fontWeight: '500', color: '#374151' }}>
                                                    {ingredient.portionQuantity} {ingredient.portionSize}
                                                </span>
                                            </div>
                                        )}
                                        {ingredient.weightGrams > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ color: '#9ca3af', fontSize: '13px' }}>Weight:</span>
                                                <span style={{ fontWeight: '500', color: '#374151' }}>
                                                    {ingredient.weightGrams}g
                                                    {ingredient.weightIsExplicit && ' (exact)'}
                                                </span>
                                            </div>
                                        )}
                                        {ingredient.barcode && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ color: '#9ca3af', fontSize: '13px' }}>Barcode:</span>
                                                <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#374151' }}>
                                                    {ingredient.barcode}
                                                </span>
                                            </div>
                                        )}
                                        {ingredient.packageType && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ color: '#9ca3af', fontSize: '13px' }}>Package:</span>
                                                <span style={{ color: '#374151' }}>{ingredient.packageType}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* No results message */}
                {results && results.length === 0 && (
                    <div style={{
                        padding: '40px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '12px',
                        textAlign: 'center',
                        color: '#6b7280',
                        border: '1px dashed #e5e7eb',
                    }}>
                        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🔍</span>
                        <div style={{ fontSize: '16px', fontWeight: '500' }}>
                            No ingredients could be predicted from the provided text.
                        </div>
                        <div style={{ fontSize: '14px', marginTop: '8px' }}>
                            Try providing more detailed recipe information.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}; 